/**
 * What Edit the setup can build (step 9.16 of
 * docs/plans/onboarding-harness/phase-09.md): the difference between the
 * workspace as it stood when the edit session was opened (its seed, version 1)
 * and the draft as it stands now, as the `SetupEditChange`s Build again writes, and
 * every other difference as an item Edit cannot build yet, with why.
 *
 * The seed is `draftFromWorkspace(…, 'edit')`: a part's path never changes once
 * minted, so a part of the draft is the built part of the same path, and
 * `built` names its id. A part the seed did not hold is new.
 *
 *   a new field on a built record      change.add
 *   a built field renamed              change.rename
 *   a built field taken out            change.archive (hidden, its values kept)
 *   a built field's must-fill or a     change.patch
 *   select's new choice
 *   a new record or list               add, with its fields and a list's names
 *   a built record left out            leave_out (archived with its entries)
 *   a built record renamed             rename_record
 *   a new feed link                    add_link
 *   a built link taken out or paused   remove_link, pause_link
 *   a built link's conditions, fills,  change_link (links-fixes A.3)
 *   sign, or turned on again
 *   a built link's from or to moved    remove_link + add_link (L5: a new
 *                                      link; the old one's rows stay)
 *   a new card, a built card taken off add_card, remove_card (a changed card is both)
 *
 * Everything else that differs is not yet: a built field's type or a pick's
 * list (`type_change`), its recipe (`formula_change`), its lines
 * (`lines_change`), a role (`role_change`, roles are changed on Team), the
 * money the workspace counts in (`currency_change`), and a part the seed kept
 * out because a draft cannot say it, or a new field of a kind Edit cannot add
 * (`kept_part`). What the draft changes that a workspace does not hold (a
 * field's help line, a record's headline) is not written and is not counted.
 *
 * Pure: the studio counts "Build again · N" with it, and the server builds it.
 */
import type { RecordBlankCounts } from './api';
import type { SetupDraftField } from './compose-author';
import type { BuiltKept } from './draft-from-workspace';
import { SETUP_CHANGE_LIMITS, SETUP_FIELD_TYPES, type SetupChangeMade, type SetupEditChange, type SetupFieldAdd, type SetupFieldPatch, type SetupFieldRename, type SetupFieldType, type SetupRecordRef } from './setup-edit';
import type { StudioHeldDirections } from './setup-draft/commit';
import type { StudioDraft, StudioLink, StudioRecord, StudioRole, StudioTile } from './setup-draft/draft';
import { studioPath } from './setup-draft/paths';

export const EDIT_NOT_YET_WHY = ['type_change', 'formula_change', 'lines_change', 'role_change', 'currency_change', 'kept_part'] as const;
export type EditNotYet = (typeof EDIT_NOT_YET_WHY)[number];

/** One difference Build again leaves as it is: the part path, why, and the part's name. */
export type EditNotYetItem = { readonly key: string; readonly why: EditNotYet; readonly name: string };

/**
 * An edit session's base (`setup_session.base`): the seed draft (version 1),
 * the id each of its part paths stands for, what the seed kept out, and each
 * built record's `field_set_ver` when it was read.
 */
export type EditBase = {
  readonly draft: StudioDraft;
  readonly built: Readonly<Record<string, string>>;
  readonly kept: readonly BuiltKept[];
  readonly fieldSetVers: Readonly<Record<string, number>>;
  /** How many entries (rows, on a list) each built record held when it was read, by record id: what OS and the Build again sheet say is kept. */
  readonly entries?: Readonly<Record<string, number>>;
};

export type EditChanges = { changes: SetupEditChange[]; notYet: EditNotYetItem[] };

/**
 * What Build again made (`POST /v1/setup/sessions/:id/build` on an edit
 * session, 9.16), kept on the session as its build result: each record it
 * touched, the first record added or changed (for "Open <record>"), and what
 * it left as it is.
 */
export type SetupEditBuildResult = {
  readonly kind: 'edit';
  readonly tenantId: string;
  readonly builtAt: string;
  /** The version built. */
  readonly version: number;
  readonly made: readonly SetupChangeMade[];
  readonly openRecordId: string | null;
  /** How many changes were built: `editChangesOf(…).changes.length`. */
  readonly changes: number;
  readonly notYet: readonly EditNotYetItem[];
};

export type SetupEditBuildResponse = { readonly status: 'built'; readonly edit: SetupEditBuildResult };

/** The catalogue key of an item's sentence (`setup.studio.edit.not_yet_<why>`). */
export const editNotYetKey = (why: EditNotYet): `setup.studio.edit.not_yet_${'type' | 'formula' | 'lines' | 'role' | 'currency' | 'kept'}` =>
  `setup.studio.edit.not_yet_${why.replace('_change', '').replace('_part', '') as 'type' | 'formula' | 'lines' | 'role' | 'currency' | 'kept'}`;

/* ---------------------------------------------------------------- helpers */

/** The same value, whatever order its keys were written in. */
const canonical = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const held = value as Record<string, unknown>;
    return `{${Object.keys(held)
      .filter((key) => held[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonical(held[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
};
const same = (a: unknown, b: unknown): boolean => canonical(a) === canonical(b);

const ADDABLE: ReadonlySet<string> = new Set(SETUP_FIELD_TYPES);

const COUNTS: Record<StudioRecord['direction'], RecordBlankCounts> = { in: 'money_in', out: 'money_out', both: 'money_both', count: 'count', none: 'none' };

const recordPath = (record: string): string => studioPath({ kind: 'record', record });
const fieldPath = (record: string, field: string): string => studioPath({ kind: 'field', record, field });
const cardPath = (record: string, card: string): string => studioPath({ kind: 'card', record, card });
const linkPath = (key: string): string => studioPath({ kind: 'link', key });
const rolePath = (key: string): string => studioPath({ kind: 'role', key });

/** The words of a list's first names: its headline's value on each seed row, at most `SETUP_CHANGE_LIMITS.rows`, once each. */
const rowsOf = (record: StudioRecord): string[] => {
  const names: string[] = [];
  for (const row of record.seedRows ?? []) {
    const value = row.values.find((one) => one.fieldKey === record.headlineFieldKey)?.value;
    const word = value === undefined ? '' : String(value).trim();
    if (word !== '' && !names.some((one) => one.toLowerCase() === word.toLowerCase())) names.push(word);
  }
  return names.slice(0, SETUP_CHANGE_LIMITS.rows);
};

/* ------------------------------------------------------------------- read */

/**
 * The draft's difference from its base, as changes Build again writes and
 * items it leaves as they are. The changes come in the draft's order: renames
 * and field changes of built records, new records and lists, records left out,
 * then links, then cards.
 */
export const editChangesOf = (base: EditBase, draft: StudioDraft): EditChanges => {
  const changes: SetupEditChange[] = [];
  const notYet: EditNotYetItem[] = [];
  const keptPaths = new Set(base.kept.map((one) => one.key));
  const note = (key: string, why: EditNotYet, name: string): void => {
    if (!notYet.some((one) => one.key === key && one.why === why)) notYet.push({ key, why, name });
  };

  const baseRecords = new Map(base.draft.records.map((record) => [record.key, record]));
  const idOf = (path: string): string | undefined => base.built[path];
  /** A record the set reaches: built (its id) or new in this draft (its key). */
  const refOf = (recordKey: string): SetupRecordRef => {
    const id = baseRecords.has(recordKey) ? idOf(recordPath(recordKey)) : undefined;
    return id === undefined ? { recordKey } : { recordId: id };
  };

  /** A new field as an add, or null with why when Edit cannot add its kind. */
  const addOf = (record: StudioRecord, field: SetupDraftField): SetupFieldAdd | null => {
    if (!ADDABLE.has(field.type)) {
      note(fieldPath(record.key, field.key), field.type === 'formula' ? 'formula_change' : field.type === 'lines' ? 'lines_change' : 'kept_part', field.label);
      return null;
    }
    const target = field.type === 'reference' && field.targetRecordKey !== undefined ? refOf(field.targetRecordKey) : null;
    return {
      key: field.key,
      label: field.label,
      type: field.type as SetupFieldType,
      ...(field.required === true ? { required: true } : {}),
      ...(field.type === 'select' && field.options !== undefined ? { options: field.options.map((option) => option.label) } : {}),
      ...(target === null ? {} : 'recordId' in target ? { targetRecordId: target.recordId } : { targetRecordKey: target.recordKey }),
    };
  };

  const cardChanges: SetupEditChange[] = [];
  const newCard = (record: StudioRecord, tile: StudioTile): void => {
    cardChanges.push({ kind: 'add_card', record: refOf(record.key), recordKey: record.key, tile: structuredClone(tile) });
  };

  /* the workspace */
  if (draft.meta.currency !== base.draft.meta.currency) note('meta', 'currency_change', draft.meta.name);

  /* records and lists the seed held */
  const draftRecords = new Map(draft.records.map((record) => [record.key, record]));
  const leftOut: SetupEditChange[] = [];
  for (const was of base.draft.records) {
    const recordId = idOf(recordPath(was.key));
    if (recordId === undefined) continue;
    const now = draftRecords.get(was.key);
    if (now === undefined) {
      leftOut.push({ kind: 'leave_out', recordId });
      continue;
    }
    if (now.name.trim() !== was.name.trim()) changes.push({ kind: 'rename_record', recordId, name: now.name.trim() });

    const add: SetupFieldAdd[] = [];
    const archive: string[] = [];
    const rename: SetupFieldRename[] = [];
    const patch: SetupFieldPatch[] = [];
    const nowFields = new Map(now.fields.map((field) => [field.key, field]));
    for (const field of was.fields) {
      const fieldId = idOf(fieldPath(was.key, field.key));
      if (fieldId === undefined) continue;
      const held = nowFields.get(field.key);
      if (held === undefined) {
        archive.push(fieldId);
        continue;
      }
      const at = fieldPath(was.key, field.key);
      if (held.type !== field.type || (held.targetRecordKey ?? null) !== (field.targetRecordKey ?? null)) {
        note(at, 'type_change', held.label);
        continue;
      }
      if (held.type === 'formula' && !same(held.formula, field.formula)) note(at, 'formula_change', held.label);
      if (held.type === 'lines' && !same(held.lines, field.lines)) note(at, 'lines_change', held.label);
      if (held.label.trim() !== field.label.trim()) rename.push({ fieldId, label: held.label.trim() });
      const one: { fieldId: string; required?: boolean; addOptions?: string[] } = { fieldId };
      if ((held.required === true) !== (field.required === true)) one.required = held.required === true;
      if (held.type === 'select') {
        const before = field.options ?? [];
        const after = held.options ?? [];
        const kept = before.every((option) => after.some((other) => other.key === option.key && other.label.trim() === option.label.trim()));
        if (!kept) note(at, 'type_change', held.label);
        else {
          const added = after.filter((option) => !before.some((other) => other.key === option.key)).map((option) => option.label.trim());
          if (added.length > 0) one.addOptions = added;
        }
      }
      if (one.required !== undefined || one.addOptions !== undefined) patch.push(one);
    }
    for (const field of now.fields) {
      if (was.fields.some((one) => one.key === field.key)) continue;
      if (keptPaths.has(fieldPath(was.key, field.key))) {
        note(fieldPath(was.key, field.key), 'kept_part', field.label);
        continue;
      }
      const made = addOf(now, field);
      if (made !== null) add.push(made);
    }
    if (add.length + archive.length + rename.length + patch.length > 0) {
      changes.push({
        kind: 'change',
        recordId,
        fieldSetVer: base.fieldSetVers[recordId] ?? 0,
        ...(add.length === 0 ? {} : { add }),
        ...(archive.length === 0 ? {} : { archive }),
        ...(rename.length === 0 ? {} : { rename }),
        ...(patch.length === 0 ? {} : { patch }),
      });
    }

    /* its cards on top */
    const wasTiles = new Map((was.tiles ?? []).map((tile) => [tile.key, tile]));
    for (const tile of was.tiles ?? []) {
      const cardId = idOf(cardPath(was.key, tile.key));
      if (cardId === undefined) continue;
      const held = (now.tiles ?? []).find((one) => one.key === tile.key);
      if (held !== undefined && same(held, tile)) continue;
      cardChanges.push({ kind: 'remove_card', recordId, cardId });
      if (held !== undefined) newCard(now, held);
    }
    for (const tile of now.tiles ?? []) {
      if (wasTiles.has(tile.key)) continue;
      if (keptPaths.has(cardPath(was.key, tile.key))) note(cardPath(was.key, tile.key), 'kept_part', tile.label);
      else newCard(now, tile);
    }
  }

  /* records and lists the seed did not hold */
  for (const record of draft.records) {
    if (baseRecords.has(record.key)) continue;
    if (keptPaths.has(recordPath(record.key))) {
      note(recordPath(record.key), 'kept_part', record.name);
      continue;
    }
    const fields = record.fields.flatMap((field) => {
      const made = addOf(record, field);
      return made === null ? [] : [made];
    });
    const rows = record.kind === 'directory' ? rowsOf(record) : [];
    changes.push({
      kind: 'add',
      key: record.key,
      recordKind: record.kind,
      name: record.name.trim(),
      ...(record.kind === 'record' ? { counts: COUNTS[record.direction] } : {}),
      fields,
      ...(rows.length === 0 ? {} : { rows }),
    });
    for (const tile of record.tiles ?? []) newCard(record, tile);
  }
  changes.push(...leftOut);

  /* links */
  const draftLinks = new Map(draft.links.map((link) => [link.key, link]));
  const baseLinks = new Set(base.draft.links.map((link) => link.key));
  for (const was of base.draft.links) {
    const linkId = idOf(linkPath(was.key));
    if (linkId === undefined) continue;
    const now = draftLinks.get(was.key);
    if (now === undefined) {
      changes.push({ kind: 'remove_link', linkId });
      continue;
    }
    // Moved to other records: a new link in its place, and the old one retired with the rows it wrote (L5).
    if (now.from !== was.from || now.to !== was.to) {
      changes.push({ kind: 'remove_link', linkId });
      changes.push({ kind: 'add_link', from: refOf(now.from), to: refOf(now.to), link: structuredClone(now), replaces: linkId });
      continue;
    }
    const { mode: wasMode, ...wasRule } = was;
    const { mode: nowMode, ...nowRule } = now;
    if (!same(wasRule, nowRule) || (wasMode === 'paused' && nowMode !== 'paused')) changes.push({ kind: 'change_link', linkId, link: structuredClone(now) });
    else if (nowMode === 'paused' && wasMode !== 'paused') changes.push({ kind: 'pause_link', linkId });
  }
  for (const link of draft.links) {
    if (baseLinks.has(link.key)) continue;
    if (keptPaths.has(linkPath(link.key))) {
      note(linkPath(link.key), 'kept_part', linkNameOf(draft, link));
      continue;
    }
    changes.push({ kind: 'add_link', from: refOf(link.from), to: refOf(link.to), link: structuredClone(link) });
  }

  changes.push(...cardChanges);

  /* roles: changed on Team */
  const roleOf = (roles: readonly StudioRole[], key: string): StudioRole | undefined => roles.find((role) => role.key === key);
  for (const key of new Set([...base.draft.roles.map((role) => role.key), ...draft.roles.map((role) => role.key)])) {
    const was = roleOf(base.draft.roles, key);
    const now = roleOf(draft.roles, key);
    if (was !== undefined && now !== undefined && same(was, now)) continue;
    note(rolePath(key), 'role_change', (now ?? was)!.name);
  }

  return { changes, notYet };
};

/** A link's name in words: "Sales → Stock". */
const linkNameOf = (draft: StudioDraft, link: StudioLink): string => {
  const name = (key: string): string => draft.records.find((record) => record.key === key)?.name ?? key;
  return `${name(link.from)} → ${name(link.to)}`;
};

/**
 * The money direction of every built record that holds at least one entry,
 * by its key, as the seed had it: an edit session's commit refuses a change
 * to one (`studioCommit`'s `heldDirections`), and so does Build again.
 * `entries` gives the counts by record id; the base's own, read when Edit was
 * opened, by default. A built record with no entries may still change.
 */
export const editHeldDirections = (base: EditBase, entries: Readonly<Record<string, number>> = base.entries ?? {}): StudioHeldDirections =>
  Object.fromEntries(
    base.draft.records.flatMap((record) => {
      const id = base.built[recordPath(record.key)];
      if (record.kind !== 'record' || id === undefined || (entries[id] ?? 0) < 1) return [];
      return [[record.key, { direction: record.direction, name: record.name }] as const];
    }),
  );

/** Reads a stored `setup_session.base`, or null when it is not one. */
export const readEditBase = (raw: unknown): EditBase | null => {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const held = raw as Record<string, unknown>;
  const draft = held.draft as StudioDraft | undefined;
  if (draft === undefined || draft === null || typeof draft !== 'object' || !Array.isArray(draft.records)) return null;
  const built = held.built !== null && typeof held.built === 'object' ? (held.built as Record<string, string>) : {};
  const kept = Array.isArray(held.kept) ? (held.kept as BuiltKept[]) : [];
  const fieldSetVers = held.fieldSetVers !== null && typeof held.fieldSetVers === 'object' ? (held.fieldSetVers as Record<string, number>) : {};
  const entries = held.entries !== null && typeof held.entries === 'object' ? (held.entries as Record<string, number>) : undefined;
  return { draft, built, kept, fieldSetVers, ...(entries === undefined ? {} : { entries }) };
};
